import { useCallback, useEffect, useRef, useState } from 'react'

interface HorizontalScrollNav {
    /** Attach to the horizontally-scrollable element. */
    scrollerRef: React.RefObject<HTMLDivElement>
    /** True when there is hidden content to the left (scrolled past the start). */
    canScrollLeft: boolean
    /** True when there is hidden content to the right (more to reveal). */
    canScrollRight: boolean
    /** Scroll by roughly one viewport in the given direction. */
    scrollByStep: (direction: 'left' | 'right') => void
}

// ─────────────────────────────────────────────────────────────────────────────
// useHorizontalScrollNav — drives the chevron + fade-edge affordances for any
// overflow-x scroller. The scroll position alone can't be derived during render
// (it lives on the DOM node), so we subscribe and re-measure on every signal
// that can change overflow:
//   • scroll           — user scrolled
//   • ResizeObserver   — the scroller box AND its content row resize. Observing
//                        the box alone misses content overflow (the box is
//                        flex-1 at a fixed width, so its clientWidth never
//                        changes when tabs are added) — so we observe the inner
//                        content row too, which grows/shrinks with the tabs.
//   • MutationObserver — tabs added/removed (childList) before they've laid out.
//   • rAF on mount     — first synchronous measure can run before fonts/layout
//                        settle, leaving scrollWidth == clientWidth; a follow-up
//                        tick catches the settled size. Without this the right
//                        chevron stays disabled until the first manual scroll.
// ─────────────────────────────────────────────────────────────────────────────
export const useHorizontalScrollNav = (): HorizontalScrollNav => {
    const scrollerRef = useRef<HTMLDivElement>(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)

    const measure = useCallback(() => {
        const el = scrollerRef.current
        if (!el) return
        // 1px slack so sub-pixel rounding doesn't leave a chevron stuck on.
        setCanScrollLeft(el.scrollLeft > 1)
        setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1)
    }, [])

    useEffect(() => {
        const el = scrollerRef.current
        if (!el) return
        measure()
        // Re-measure after layout/fonts settle so the right chevron is correctly
        // enabled on first paint instead of waiting for a manual scroll.
        const raf = requestAnimationFrame(measure)

        el.addEventListener('scroll', measure, { passive: true })

        const ro = new ResizeObserver(measure)
        ro.observe(el)
        // Observe the content row too — its width tracks the tabs, so this fires
        // when content overflow appears even though the box size is unchanged.
        const content = el.firstElementChild
        if (content) ro.observe(content)

        const mo = new MutationObserver(measure)
        mo.observe(el, { childList: true, subtree: true })

        return () => {
            cancelAnimationFrame(raf)
            el.removeEventListener('scroll', measure)
            ro.disconnect()
            mo.disconnect()
        }
    }, [measure])

    const scrollByStep = useCallback((direction: 'left' | 'right') => {
        const el = scrollerRef.current
        if (!el) return
        const step = Math.max(el.clientWidth * 0.8, 160)
        el.scrollBy({ left: direction === 'left' ? -step : step, behavior: 'smooth' })
    }, [])

    return { scrollerRef, canScrollLeft, canScrollRight, scrollByStep }
}
